import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { AutomationRulesContext, AutomationRulesProvider } from '../../../../../../../settings/automation-rules/context';
import type { AutomationRule } from '../../../../../../../settings/automation-rules/types/automation-rule-types';
import { FilterEditorContextProvider } from '../../../../../../../../components/filter-editor/context';
import { RuleEditorDrawer } from '../../../../../../../settings/automation-rules/modals/rule-editor-drawer';
import { useOpenRuleDrawer } from '../../../../../../../settings/automation-rules/hooks/use-open-rule-drawer';
import {
  prepopulatedInsuranceRule,
  prepopulatedInsuranceFilter,
} from '../../../../../../../home-page/components/engagement-section/engagement-action-buttons/prepopulated-insurance-rule';
import { FTU_INSURANCE_RULE_CREATED } from '../../../../../../../../constants/analytics-events';
import { INSURANCE_PREFERENCE, saveInsurancePreference } from '../../../../../../../../hooks/use-insurance-preference';
import { getAutomationsSettingsPath } from '../../../../../../../../routes/constants';
import { Segment } from '../../../../../../../../services/analytics-services/segment';
import { useWalletedInsuranceProviderId } from '../../../../../../../../services/insurance-service';
import { saveNewSellerSettings } from '../../../../../../../../store/seller/seller.actions';
import { useSystemAutomation } from '../../../../../../../../contexts/system-automation-context';
import { SYSTEM_RULE_NAMES } from '../../../../../../../../contexts/system-automation-context/constants';
import { messages as walletedInsuranceMessages } from '../../../../messages';
import { insureByRulesMessages } from './messages';
import styles from './insure-by-rules-content.module.less';

type InsureByRulesContentProps = {
  hasActiveInsuranceRule: boolean;
  hasValidationError: boolean;
};

const CreateInsuranceRuleButton = () => {
  const { isDrawerOpen } = useContext(AutomationRulesContext);
  const openRuleDrawer = useOpenRuleDrawer();
  const { refreshAutomationRules, getSystemRule } = useSystemAutomation();
  const wasDrawerOpenRef = useRef(false);

  useEffect(() => {
    if (isDrawerOpen) {
      wasDrawerOpenRef.current = true;
      return;
    }

    if (wasDrawerOpenRef.current) {
      wasDrawerOpenRef.current = false;
      // Capture pre-refresh state so we can detect whether a new rule was just created
      const hadRuleBefore = getSystemRule(SYSTEM_RULE_NAMES.INSURE_BY_RULES)?.active ?? false;
      refreshAutomationRules().then(() => {
        if (!hadRuleBefore) {
          Segment.trackEvent(FTU_INSURANCE_RULE_CREATED, {
            page: 'Insurance Settings',
          });
        }
      });
    }
  }, [isDrawerOpen, getSystemRule, refreshAutomationRules]);

  const handleClick = useCallback(() => {
    const { active, orderRuleActions, isNew } = prepopulatedInsuranceRule;
    openRuleDrawer({
      rule: { active, orderRuleActions, isNew, name: SYSTEM_RULE_NAMES.INSURE_BY_RULES },
      name: SYSTEM_RULE_NAMES.INSURE_BY_RULES,
      filterSet: { active: false, appliedFilters: {} },
    });
  }, [openRuleDrawer]);

  return (
    <>
      <Button className={styles['create-rule-link']} onClick={handleClick} theme={THEME_CONSTANTS.link}>
        <Text textType={TextType.Paragraph} value={insureByRulesMessages.createInsuranceRule} />
      </Button>
      {isDrawerOpen && <RuleEditorDrawer />}
    </>
  );
};

const CreateInsuranceRuleWithContext = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const insuranceProviderId = useWalletedInsuranceProviderId();

  // Persist the insure-by-rules preference when the drawer save succeeds. The drawer replaces
  // the settings-page Save Changes interaction, so we have to sync the seller setting here —
  // otherwise the preference is lost if the rule is later deleted or deactivated.
  const handleRuleSaved = useCallback(
    (rule: AutomationRule) => {
      if (rule.name !== SYSTEM_RULE_NAMES.INSURE_BY_RULES || !rule.active || !insuranceProviderId) return;

      saveInsurancePreference({
        preference: INSURANCE_PREFERENCE.INSURE_BY_RULES,
        insuranceProviderId,
        existingSystemRule: rule,
        shouldWritePreferenceNone: true,
      })
        .then((newSettings) => dispatch(saveNewSellerSettings(newSettings)))
        .catch(() => {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            content: intl.formatMessage(walletedInsuranceMessages.savePreferencesError),
          });
        });
    },
    [dispatch, insuranceProviderId, intl],
  );

  return (
    <AutomationRulesProvider onRuleSaved={handleRuleSaved}>
      <FilterEditorContextProvider preconfiguredFilter={prepopulatedInsuranceFilter}>
        <CreateInsuranceRuleButton />
      </FilterEditorContextProvider>
    </AutomationRulesProvider>
  );
};

export const InsureByRulesContent = ({ hasActiveInsuranceRule, hasValidationError }: InsureByRulesContentProps) => {
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const handleDismissBanner = useCallback(() => {
    setIsBannerDismissed(true);
  }, []);

  return (
    <FlexColumn className={styles['insure-by-rules-content']} gap={Gap.Space4}>
      <Text
        className={styles['automation-rules-description']}
        formattedMessageValues={{
          a: (chunks: React.ReactNode) => <Link to={getAutomationsSettingsPath()}>{chunks}</Link>,
        }}
        textType={TextType.Description}
        value={insureByRulesMessages.automationRulesDescription}
      />
      {hasActiveInsuranceRule ? (
        !isBannerDismissed && (
          <BannerMessageV2
            bannerContainerClassName={styles['active-rules-banner-container']}
            className={styles['active-rules-banner']}
            content={
              <Text
                formattedMessageValues={{
                  a: (chunks: React.ReactNode) => (
                    <Link className={styles['view-rules-link']} to={getAutomationsSettingsPath()}>
                      {chunks}
                    </Link>
                  ),
                }}
                textType={TextType.Paragraph}
                value={insureByRulesMessages.activeRulesBanner}
              />
            }
            onClose={handleDismissBanner}
            status={BannerStatus.Info}
          />
        )
      ) : (
        <>
          <CreateInsuranceRuleWithContext />
          {hasValidationError && (
            <BannerMessageV2
              className={styles['validation-error-banner']}
              content={<Text textType={TextType.Paragraph} value={insureByRulesMessages.validationError} />}
              status={BannerStatus.Error}
            />
          )}
        </>
      )}
    </FlexColumn>
  );
};
